import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { getMenu } from '../services/MenuService';
import AuthContext from './AuthContext';
import i18n from '../i18next';
import { canAccessAccounting, isAccountingOnlyPermissions } from '../utils/accountingAccess';
import PlatformBridge from '../utils/PlatformBridge';

const MenuContext = createContext();
const SUPER_ADMIN_ONLY_MENU_PATHS = new Set(['/roles']);

const groupLocationsMenu = (menuItems) => {
  if (!Array.isArray(menuItems)) return [];

  const locationItems = menuItems.filter(
    (item) => item?.icon === 'location' && Array.isArray(item.submenu) && item.submenu.length > 0
  );

  if (locationItems.length <= 1) {
    return menuItems;
  }

  const groupedMenu = [];
  let inserted = false;

  menuItems.forEach((item) => {
    if (locationItems.includes(item)) {
      if (!inserted) {
        groupedMenu.push({
          id: 'locations_group',
          title: 'Locales',
          path: '',
          icon: 'storefront',
          submenu: locationItems
        });
        inserted = true;
      }
      return;
    }

    groupedMenu.push(item);
  });

  return groupedMenu;
};

const filterAccountingMenu = (menuItems) => {
  if (!Array.isArray(menuItems)) return [];

  const roleName = localStorage.getItem('role') || '';
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const accountingAllowed = canAccessAccounting({ roleName, permissions });
  const accountingOnly = isAccountingOnlyPermissions(permissions);

  if (accountingOnly) {
    const accountingItem = findMenuItemByPath(menuItems, '/accounting') || {
      id: 'accounting',
      title: 'Contabilidad',
      path: '/accounting',
      icon: 'analytics',
      permissions: []
    };
    return [accountingItem];
  }

  return menuItems.reduce((acc, item) => {
    if (!item) return acc;

    if (item.path === '/accounting' && !accountingAllowed) {
      return acc;
    }

    const nextItem = Array.isArray(item.submenu)
      ? { ...item, submenu: filterAccountingMenu(item.submenu) }
      : item;

    acc.push(nextItem);
    return acc;
  }, []);
};

const filterSuperAdminOnlyMenu = (menuItems) => {
  if (!Array.isArray(menuItems)) return [];

  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  if (isSuperAdmin) return menuItems;

  return menuItems.reduce((acc, item) => {
    if (!item || SUPER_ADMIN_ONLY_MENU_PATHS.has(item.path)) {
      return acc;
    }

    const nextItem = Array.isArray(item.submenu)
      ? { ...item, submenu: filterSuperAdminOnlyMenu(item.submenu) }
      : item;

    if (Array.isArray(nextItem.submenu) && nextItem.submenu.length === 0 && !nextItem.path) {
      return acc;
    }

    acc.push(nextItem);
    return acc;
  }, []);
};

const findMenuItemByPath = (menuItems, path) => {
  if (!Array.isArray(menuItems)) return null;

  for (const item of menuItems) {
    if (item?.path === path) return item;
    const submenuItem = findMenuItemByPath(item?.submenu, path);
    if (submenuItem) return submenuItem;
  }

  return null;
};

const normalizeMenu = (menuItems) => filterAccountingMenu(filterSuperAdminOnlyMenu(groupLocationsMenu(menuItems)));

export const MenuProvider = ({ children }) => {
  const { accessToken } = useContext(AuthContext);
  
  // Cargar menú desde localStorage al inicio si existe
  const loadMenuFromStorage = () => {
    try {
      const storedMenu = localStorage.getItem('menu');
      if (storedMenu) {
        const parsedMenu = JSON.parse(storedMenu);
        if (Array.isArray(parsedMenu) && parsedMenu.length > 0) {
          return normalizeMenu(parsedMenu);
        }
      }
    } catch (error) {
      console.error('Error loading menu from localStorage:', error);
    }
    return [];
  };

  const [menu, setMenu] = useState(() => normalizeMenu(loadMenuFromStorage()));
  const [isMenuLoaded, setIsMenuLoaded] = useState(() => {
    // Si hay menú guardado, marcar como cargado desde el inicio
    const storedMenu = loadMenuFromStorage();
    return storedMenu.length > 0;
  });
  const [menuLoadingError, setMenuLoadingError] = useState(null);

  const fetchMenu = useCallback(async () => {
    // Obtener el menú actual desde localStorage como respaldo
    const storedMenu = loadMenuFromStorage();
    
    try {
      const response = await getMenu();
      // Asegurarse de que la respuesta sea un array válido
      if (Array.isArray(response) && response.length > 0) {
        const normalizedMenu = normalizeMenu(response);
        setMenu(normalizedMenu);
        localStorage.setItem('menu', JSON.stringify(normalizedMenu));
        setIsMenuLoaded(true);
        setMenuLoadingError(null);
        return normalizedMenu;
      } else {
        // Si la respuesta no es válida, mantener el menú guardado
        console.warn('Menu response is not valid, keeping stored menu');
        if (storedMenu.length > 0) {
          setMenu(normalizeMenu(storedMenu));
          setIsMenuLoaded(true);
          setMenuLoadingError(null);
          return normalizeMenu(storedMenu);
        }
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
      // Si falla, mantener el menú guardado (NO limpiar)
      if (storedMenu.length > 0) {
        console.log('Keeping stored menu after error');
        setMenu(normalizeMenu(storedMenu));
        setIsMenuLoaded(true);
        setMenuLoadingError(null);
        return normalizeMenu(storedMenu);
      }
      // Solo si no hay menú guardado, marcar como error pero NO limpiar el menú actual del estado
      setMenuLoadingError('Error fetching menu');
      setIsMenuLoaded(false);
      return false; // 👈 para que lo pueda usar el login
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      // Verificar si hay accessToken en el storage también (por si el contexto aún no se ha actualizado)
      const storedAccessToken = await PlatformBridge.getToken('accessToken');
      if (!active) return;
      const hasToken = accessToken || storedAccessToken;

      if (!hasToken) {
        // Solo limpiar si realmente no hay token Y no hay menú guardado
        const storedMenu = loadMenuFromStorage();
        if (storedMenu.length === 0) {
          setMenu([]);
          setIsMenuLoaded(false);
          setMenuLoadingError(null);
        }
      } else {
        // Si hay token, asegurarse de que tenemos menú cargado
        const storedMenu = loadMenuFromStorage();
        if (storedMenu.length > 0) {
          // Si hay menú guardado, SIEMPRE mantenerlo (incluso si ya hay menú en el estado)
          setMenu(normalizeMenu(storedMenu));
          setIsMenuLoaded(true);
          // Intentar actualizar desde el backend en segundo plano (esto no limpiará el menú si falla)
          fetchMenu();
        } else if (accessToken || storedAccessToken) {
          // Si no hay menú guardado pero hay token, intentar cargar
          fetchMenu();
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, fetchMenu]);

  useEffect(() => {
    // Definir el oyente para el cambio de idioma
    const handleLanguageChange = () => {
      if (accessToken) {
        fetchMenu();
      }
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [accessToken, fetchMenu]);  

  return (
    <MenuContext.Provider value={{ menu, isMenuLoaded, menuLoadingError, fetchMenu }}>
      {children}
    </MenuContext.Provider>
  );
};

export default MenuContext;
